import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  Timestamp,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

// The persisted message shape IS the in-memory shape. Previously this file
// declared a stripped-down ChatMessage subset, so card payloads (mealPlan,
// sources, researchCard, goalPlan, suggestedReplies, proposed records, …) were
// silently dropped on save and never rehydrated — cards vanished on reload.
// Use the store's canonical type and serialize/deserialize ALL fields.
import type { ChatMessage } from '../stores/chatSession';
export type { ChatMessage };

/** Transient, local-only message fields that must never be persisted. */
const TRANSIENT_MESSAGE_FIELDS = ['attachmentPreviewUrls'] as const;

/**
 * Convert an in-memory message to a Firestore-safe object. Persists every card
 * field present. Firestore rejects `undefined` anywhere in a document, so we
 * deep-strip via a JSON round-trip — lossless here because all card payloads
 * originate from JSON SSE events (no live Date/function values nested inside).
 * The top-level `timestamp` is the one real Date, handled explicitly.
 */
function serializeMessage(msg: ChatMessage): Record<string, any> {
  const { timestamp, attachments, ...rest } = msg;
  for (const f of TRANSIENT_MESSAGE_FIELDS) delete (rest as any)[f];
  const cleaned = JSON.parse(JSON.stringify(rest));
  return {
    ...cleaned,
    timestamp: toFirestoreDate(timestamp),
    // Only persist attachments when present (avoid storing empty arrays).
    ...(attachments && attachments.length > 0 && { attachments }),
  };
}

/** Rehydrate a persisted message back into the in-memory shape. */
function deserializeMessage(raw: any): ChatMessage {
  const { timestamp, ...rest } = raw || {};
  return { ...rest, timestamp: toDate(timestamp) } as ChatMessage;
}

export interface ChatConversation {
  id: string;
  userId: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Convert Firestore timestamp to Date
 */
function toDate(value: any): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (value && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
  }
  return new Date(value);
}

/**
 * Convert Date to Firestore-compatible format
 */
function toFirestoreDate(date: Date): any {
  return Timestamp.fromDate(date);
}

/**
 * Get all chat conversations for a user
 */
export async function getUserChats(userId: string): Promise<ChatConversation[]> {
  try {
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const chats: ChatConversation[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      chats.push({
        id: doc.id,
        userId: data.userId,
        title: data.title || 'New Chat',
        messages: (data.messages || []).map(deserializeMessage),
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
      });
    });

    return chats;
  } catch (error) {
    console.error('Error fetching user chats:', error);
    throw error;
  }
}

/**
 * Get a specific chat conversation
 */
export async function getChat(chatId: string): Promise<ChatConversation | null> {
  try {
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    
    if (!chatSnap.exists()) {
      return null;
    }
    
    const data = chatSnap.data();
    return {
      id: chatSnap.id,
      userId: data.userId,
      title: data.title || 'New Chat',
      messages: (data.messages || []).map(deserializeMessage),
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
    };
  } catch (error) {
    console.error('Error fetching chat:', error);
    throw error;
  }
}

/**
 * Create a new chat conversation
 */
export async function createChat(
  userId: string,
  initialMessage?: ChatMessage
): Promise<string> {
  try {
    const messages = initialMessage ? [initialMessage] : [];
    const title = initialMessage 
      ? initialMessage.content.substring(0, 50) + (initialMessage.content.length > 50 ? '...' : '')
      : 'New Chat';
    
    const chatData = {
      userId,
      title,
      messages: messages.map(serializeMessage),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const chatsRef = collection(db, 'chats');
    const newChatRef = doc(chatsRef);
    await setDoc(newChatRef, chatData);
    
    return newChatRef.id;
  } catch (error) {
    console.error('Error creating chat:', error);
    throw error;
  }
}

/**
 * Update a chat conversation with new messages
 */
export async function updateChat(
  chatId: string,
  messages: ChatMessage[],
  title?: string
): Promise<void> {
  try {
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    
    if (!chatSnap.exists()) {
      throw new Error('Chat not found');
    }
    
    const updateData: any = {
      messages: messages.map(serializeMessage),
      updatedAt: serverTimestamp(),
    };
    
    // Update title if provided or if it's still "New Chat" and we have messages
    if (title) {
      updateData.title = title;
    } else if (messages.length > 0) {
      const firstUserMessage = messages.find(m => m.role === 'user' && !m.isNudge);
      if (firstUserMessage) {
        const newTitle = firstUserMessage.content.substring(0, 50) +
          (firstUserMessage.content.length > 50 ? '...' : '');
        updateData.title = newTitle;
      }
    }
    
    await updateDoc(chatRef, updateData);
  } catch (error) {
    console.error('Error updating chat:', error);
    throw error;
  }
}

/**
 * Delete a chat conversation
 */
export async function deleteChat(chatId: string): Promise<void> {
  try {
    const chatRef = doc(db, 'chats', chatId);
    await deleteDoc(chatRef);
  } catch (error) {
    console.error('Error deleting chat:', error);
    throw error;
  }
}

